// Lazy loading system for optional features

export interface LazyModule<T = any> {
  load: () => Promise<T>
  isLoaded: boolean
  module?: T
}

export class LazyLoader {
  private modules: Map<string, LazyModule> = new Map()

  register<T>(name: string, loader: () => Promise<T>): void {
    this.modules.set(name, {
      load: loader,
      isLoaded: false
    })
  }

  async load<T>(name: string): Promise<T> {
    const module = this.modules.get(name)
    if (!module) {
      throw new Error(`Lazy module '${name}' is not registered`)
    }

    if (module.isLoaded && module.module) {
      return module.module as T
    }

    try {
      const loadedModule = await module.load()
      module.module = loadedModule
      module.isLoaded = true
      return loadedModule as T
    } catch (error) {
      throw new Error(`Failed to load lazy module '${name}': ${error}`)
    }
  }

  isLoaded(name: string): boolean {
    const module = this.modules.get(name)
    return module?.isLoaded || false
  }

  preload(name: string): Promise<void> {
    return this.load(name).then(() => {})
  }

  preloadAll(): Promise<void[]> {
    const promises = Array.from(this.modules.keys()).map(name => this.preload(name))
    return Promise.all(promises)
  }
}

// Global lazy loader instance
export const lazyLoader = new LazyLoader()

// Lazy loading utilities
export function lazy<T>(name: string, loader: () => Promise<T>): () => Promise<T> {
  lazyLoader.register(name, loader)
  return () => lazyLoader.load<T>(name)
}

export function lazyImport<T>(name: string, importFn: () => Promise<T>): () => Promise<T> {
  return lazy(name, importFn)
}

// Framework-specific lazy loaders
export const lazyReact = lazy('react', async () => {
  try {
    const react = await import('react')
    return react
  } catch {
    throw new Error('React is not installed')
  }
}) as () => Promise<any>
export const lazyNext = lazy('next', async () => {
  try {
    const next = await import('next' as any)
    return next
  } catch {
    throw new Error('Next.js is not installed')
  }
}) as () => Promise<any>
export const lazyExpress = lazy('express', async () => {
  try {
    const express = await import('express')
    return express
  } catch {
    throw new Error('Express is not installed')
  }
}) as () => Promise<any>

// Adapter lazy loaders
// Note: Production adapters are in dedicated packages (@kitium/auth-postgres, @kitium/auth-stripe, @kitium/auth-redis)
// Only memory adapter is available here

// Component lazy loaders
export const lazySignIn = lazy('signin', () => import('../components/SignIn'))
export const lazyUserMenu = lazy('usermenu', () => import('../components/UserMenu'))
export const lazyBillingPortal = lazy('billingportal', () => import('../components/BillingPortal'))

// Framework integration lazy loaders
export const lazyNextAuth = lazy('next-auth', () => import('../frameworks/next'))
export const lazyExpressAuth = lazy('express-auth', () => import('../frameworks/express'))
export const lazyReactAuth = lazy('react-auth', () => import('../frameworks/react'))

// CLI lazy loaders
// Note: CLI is in dedicated package (@kitium/auth-cli)

// Utility functions for conditional loading
export async function loadIfAvailable<T>(name: string, fallback?: T): Promise<T | undefined> {
  try {
    return await lazyLoader.load<T>(name)
  } catch (error) {
    if (fallback !== undefined) {
      return fallback
    }
    return undefined
  }
}

export function createConditionalLoader<T>(
  condition: () => boolean,
  loader: () => Promise<T>,
  fallback?: T
): () => Promise<T | undefined> {
  return async () => {
    if (condition()) {
      try {
        return await loader()
      } catch (error) {
        return fallback
      }
    }
    return fallback
  }
}

// Environment-based lazy loading
// Environment targets not implemented in this package; stubs removed to avoid unresolved modules

// Feature flag-based lazy loading
export function createFeatureLoader<T>(
  featureFlag: string,
  loader: () => Promise<T>,
  fallback?: T
): () => Promise<T | undefined> {
  return createConditionalLoader(
    () => process.env[`KITIUM_FEATURE_${featureFlag.toUpperCase()}`] === 'true',
    loader,
    fallback
  )
}
